import { CreateCustomLevelInput } from '@/common/types/customLevel';
import {
  getGraphemesBySpelling,
  getSentenceBySpelling,
  getWordBySpelling,
} from '@/common/utils/scopeAndSequence';
import { ActivityType, parseHTMLToSections } from './utils';

function randomSample<T>(arr: T[], n: number): T[] {
  const copy = [...arr];
  for (let i = copy.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [copy[i], copy[j]] = [copy[j], copy[i]];
  }
  return copy.slice(0, n);
}

export function generateCustomLevelFromContent(
  html: string,
  lessonPlanName: string,
): Omit<CreateCustomLevelInput, 'createdBy' | 'organisationId'> {
  const sections = parseHTMLToSections(html);

  const mysticMatchIds: string[] = [];
  const wordWeaverIds: string[] = [];
  const wordSumsIds: string[] = [];
  const phonemeForgeIds: string[] = [];
  const sentenceBuilderIds: string[] = [];

  for (const section of sections) {
    switch (section.type) {
      case ActivityType.SOUND_RECOGNITION_DRILL: {
        for (const item of section.content) {
          if (typeof item.value !== 'string') continue;
          const parts = item.value.split('|');
          if (parts.length !== 2) continue;
          const [spelling, pronunciation] = parts;
          const grapheme = getGraphemesBySpelling(spelling.trim()).find(
            (g) => g.pronunciation === pronunciation.trim(),
          );
          if (grapheme && !mysticMatchIds.includes(grapheme.id)) {
            mysticMatchIds.push(grapheme.id);
          }
        }
        break;
      }

      case ActivityType.SPELLING_DRILL: {
        for (const item of section.content) {
          if (typeof item.value !== 'string') continue;
          const word = getWordBySpelling(item.value.trim());
          if (word && !wordWeaverIds.includes(word.id)) {
            wordWeaverIds.push(word.id);
          }
        }
        break;
      }

      case ActivityType.WORD_SUMS: {
        for (const item of section.content) {
          if (typeof item.value !== 'string') continue;
          const eqIdx = item.value.indexOf('=');
          if (eqIdx === -1) continue;
          const spelling = item.value.substring(eqIdx + 1).trim();
          const word = getWordBySpelling(spelling);
          if (word && !wordSumsIds.includes(word.id)) {
            wordSumsIds.push(word.id);
          }
        }
        break;
      }

      case ActivityType.DECODING_WORD_CHAIN:
      case ActivityType.SPELLING_WORD_CHAIN: {
        for (const item of section.content) {
          if (typeof item.value !== 'string') continue;
          const spellings = item.value.split(',');
          for (const spelling of spellings) {
            const word = getWordBySpelling(spelling.trim());
            if (word && !phonemeForgeIds.includes(word.id)) {
              phonemeForgeIds.push(word.id);
            }
          }
        }
        break;
      }

      case ActivityType.SENTENCE_DICTATION: {
        for (const item of section.content) {
          if (typeof item.value !== 'string') continue;
          const sentence = getSentenceBySpelling(item.value.trim());
          if (sentence && !sentenceBuilderIds.includes(sentence.id)) {
            sentenceBuilderIds.push(sentence.id);
          }
        }
        break;
      }
    }
  }

  const MAX_GRAPHEMES = 6;
  const MAX_WORDS = 10;
  const MAX_SENTENCES = 5;

  const mysticMatch = randomSample(mysticMatchIds, MAX_GRAPHEMES);
  const wordWeaver = randomSample(wordWeaverIds, MAX_WORDS);
  const wordSums = randomSample(wordSumsIds, MAX_WORDS);
  const sentenceBuilder = randomSample(sentenceBuilderIds, MAX_SENTENCES);

  return {
    description: lessonPlanName,
    students: {},
    mysticMatch: {
      enabled: mysticMatch.length > 0,
      items: mysticMatch as CreateCustomLevelInput['mysticMatch']['items'],
    },
    wordWeaver: {
      enabled: wordWeaver.length > 0,
      items: wordWeaver as CreateCustomLevelInput['wordWeaver']['items'],
    },
    wordSums: {
      enabled: wordSums.length > 0,
      items: wordSums as CreateCustomLevelInput['wordSums']['items'],
    },
    phonemeForge: {
      enabled: phonemeForgeIds.length > 0,
      items: phonemeForgeIds as CreateCustomLevelInput['phonemeForge']['items'],
    },
    morphMaster: {
      enabled: false,
      items: [],
    },
    sentenceBuilder: {
      enabled: sentenceBuilder.length > 0,
      items:
        sentenceBuilder as CreateCustomLevelInput['sentenceBuilder']['items'],
    },
  };
}
